import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import { Dialog, Foot, Loader } from '../CompanyWorkspace/dialog';
import SplitLeg from './SplitLeg';
import PrepaidSentence, { prepaidHoldOf, HandDoorLock } from './handPrepaid';
import { EDIT_REF_KEY } from '../CompanyWorkspace/JournalBook';
import { RecordEvidence } from '../CompanyWorkspace/attachments';   // owner 2026-11-02: a draft's supporting files and linked records; nothing typed above is sent by them
import type { JnlDraftEditInput, JnlEditableDraft } from '../../types';

// ── A draft entry, edited by permission (batch 12 tail T4, COA-D11; the customer's link doc §6) ────────────────────────
// ONE verdict decides what this window offers — the server's (server/src/services/draftEdit.ts draftEditOf, read with the
// draft through GET /journal/draft/:entryId), never a guess made here:
//   · full   — a manual draft (typed, imported, copied): the New form's header and a grid of every line — account, Dr, Cr,
//              PARTY, REFERENCE and LINE DESCRIPTION (an editor of {account, dr, cr} alone would empty the party, the ref and
//              the memo the books carry on 47 % / 31 % / 41 % of manual lines), add and remove, the totals and whether they
//              agree, an optional "why" kept with the edit. ONE PUT with the version read; a draft changed meanwhile answers
//              409 draft_changed — it is read again and the window says so.
//   · review — a draft generated from a VAT line: its lines as they stand, an account picker and Split on its FREE legs only
//              (not the party's, VAT or cash line), the pickers listing only the accounts such a leg may go to (the
//              account list's `free`) — the existing account and split doors do the writing. A sentence says the rest is
//              corrected on the document.
//   · none   — nothing is offered; the reason, in words (callers open this window only for a verdict that is set).
// ⛔ A request bound to be refused is never sent: Save waits until something was typed, the lines balance and every rule New
//    applies that can be read here holds (a description, two lines with an account and one side each, the calc note of an
//    internal voucher, the bank account of a bank-book entry, the date in the draft's period — and in its month for a JV
//    number —, a reversal date after the period, an account a line may take); a refusal holds Save until the form changes.
// ⛔ A JV number is the system's and stays (TL-22): shown as a fact, never a field; its date is bounded to its month.
// ⛔ The source a draft was made for by hand is shown as a fixed banner and never sent; such a draft takes no reversal date.
//    (The tail's review) For a statement line's, the hand door is asked FIRST, about the draft itself (GET /journal/hand-door
//    with exceptEntryId — the PUT asks the same lock with the draft's own claim excepted), and Save is held while the lines
//    debit the WHT-prepaid account beyond what the door takes, in the New form's words (./handPrepaid — one copy of both).
// ⛔ A read-only seat never reaches this window (its callers offer no Edit to one); if it did, it would offer no Save.
// ⛔ Amounts are typed in the books' units and sent as integer satang — one conversion, here.

const BOOKS: [string, string][] = [['general', 'pa_jnl_general'], ['cash', 'pa_jnl_cash'], ['bank', 'pa_jnl_bank'], ['purchase', 'pa_jnl_purchase'], ['sale', 'pa_jnl_sale']];
const HAND_FOR_KEY: Record<string, string> = { tax_line: 'pa_manual_for_tax_line', bank_line: 'pa_manual_for_bank_line', cash_line: 'pa_manual_for_cash_line' };
const PROBLEM_KEY: Record<string, string> = { switched_off: 'pa_edit_account_switched_off', not_postable: 'pa_edit_account_not_postable', missing: 'pa_edit_account_missing' };
type Account = { id: string; code: string; name_th: string; name_en: string; class: string; free?: boolean };
/** One line of the grid: amounts as typed; `docRef` / `narration` '' while they follow the header (createManualEntry's own
 *  default, `docRef || docNo`, `narration || narration` — the server re-defaults an empty one from the header it saves). */
type Row = { key: string; id: string; accountId: string; dr: string; cr: string; partnerId: string; partnerName: string; origPartner: string;
  docRef: string; narration: string; problem: string; code: string };

const satang = (v: string) => Math.round((parseFloat(String(v || '').replace(/,/g, '')) || 0) * 100);
const typed = (s: number) => (s ? (s / 100).toFixed(2) : '');
const baht = (s: number) => money(Number(s || 0) / 100);
const lastDay = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`; };
let rowSeq = 0;

const cell: React.CSSProperties = { padding: '4px 5px', verticalAlign: 'top' };
const inpS: React.CSSProperties = { padding: '6px 8px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box', width: '100%' };
const lblS: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const factS: React.CSSProperties = { fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)', padding: '6px 0' };
const noteS: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 7, padding: '7px 10px', lineHeight: 1.55 };
const warnS: React.CSSProperties = { fontSize: 12, color: '#B42318', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 7, padding: '7px 10px', lineHeight: 1.55 };
const smallBtn: React.CSSProperties = { padding: '4px 9px', fontSize: 11.5, fontWeight: 700, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', cursor: 'pointer' };

export default function DraftEditDialog({ entryId, onClose, onSaved, onChanged }: {
  entryId: string;
  onClose: () => void;
  /** Saved: the window closes; the caller reads its list again and may say `message`. */
  onSaved: (message: string) => void;
  /** A split made through the split door from inside this window (review): the caller's list is now out of date. */
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [d, setD] = useState<JnlEditableDraft | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [bankAccts, setBankAccts] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  // The body of the last request the server REFUSED (4xx): Save is held while the form still says exactly that.
  const [held, setHeld] = useState('');
  // full
  const [date, setDate] = useState('');
  const [book, setBook] = useState('general');
  const [bankAcct, setBankAcct] = useState('');
  const [docNo, setDocNo] = useState('');
  const [narration, setNarration] = useState('');
  const [calcNote, setCalcNote] = useState('');
  const [reverseOn, setReverseOn] = useState('');
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  // review: the account chosen per free leg (staged until Save), and the leg whose split editor is open
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [splitting, setSplitting] = useState('');
  // full, a draft recorded by hand for a statement line: the hand door's WHT-prepaid lock asked about this draft — `undefined`
  // while it is read (Save waits for it), null when nothing is locked or the draft is no statement line's.
  const [handLock, setHandLock] = useState<HandDoorLock | null | undefined>(null);

  const fill = useCallback((x: JnlEditableDraft) => {
    setD(x);
    setDate(x.date); setBook(x.book || 'general'); setBankAcct(x.bankAccountId || ''); setDocNo(x.jv ? '' : x.docNo);
    setNarration(x.narration); setCalcNote(x.calcNote); setReverseOn(x.autoReverseOn); setReason('');
    setRows(x.lines.map(l => ({ key: `r${++rowSeq}`, id: l.id, accountId: l.accountId, dr: typed(l.dr), cr: typed(l.cr), partnerId: l.partnerId, partnerName: l.partnerName,
      origPartner: l.partnerName, docRef: l.docRef === x.docNo ? '' : l.docRef, narration: l.narration === x.narration ? '' : l.narration, problem: l.accountProblem, code: l.code })));
    setChosen({}); setSplitting(''); setDirty(false); setHeld('');
  }, []);
  const load = useCallback(async (): Promise<JnlEditableDraft | null> => {
    try { const x = await api.jnlDraft(entryId); fill(x); setLoadErr(''); return x; }
    catch (e: any) { setLoadErr(String(e?.message || e)); return null; }
  }, [entryId, fill]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d?.clientId) return;
    api.jnlAccounts(d.clientId).then((r: any) => setAccounts(r.accounts || [])).catch(() => setAccounts([]));
    api.jnlBankAccounts(d.clientId).then((r: any) => setBankAccts((r.accounts || []).map((a: any) => String(a.id)))).catch(() => setBankAccts([]));
  }, [d?.clientId]);

  const scope = d?.edit?.scope || null;
  const handLine = d && scope === 'full' && d.hand?.kind === 'bank_line' ? d.hand.id : '';
  useEffect(() => {
    if (!d || !handLine) { setHandLock(null); return; }
    let live = true;
    setHandLock(undefined);
    api.jnlHandDoor(d.clientId, 'bank_line', handLine, d.entryId)
      .then((r: any) => { if (live) setHandLock(r?.prepaid && Array.isArray(r.prepaid.accountIds) ? r.prepaid : null); })
      .catch(() => { if (live) setHandLock(null); });   // the door still asks; a read that failed holds nothing back
    return () => { live = false; };
  }, [d?.clientId, d?.entryId, d?.version, handLine]);   // eslint-disable-line react-hooks/exhaustive-deps
  const touch = () => { setDirty(true); setErr(''); };
  const setRow = (key: string, patch: Partial<Row>) => { touch(); setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r))); };

  // ── full: the body, and what New's rules would refuse of it, read here before anything is sent ─────────────────────
  const kept = rows.filter(r => r.accountId || satang(r.dr) || satang(r.cr));
  const dr = kept.reduce((s, r) => s + satang(r.dr), 0);
  const cr = kept.reduce((s, r) => s + satang(r.cr), 0);
  const balanced = dr === cr && dr > 0;
  const body = (): JnlDraftEditInput | null => (d ? {
    version: d.version, entryDate: date, sourceBook: book, bankAccountId: book === 'bank' ? bankAcct.trim() : '',
    docNo: d.jv ? d.docNo : docNo.trim(), narration: narration.trim(), calcNote: calcNote.trim(), autoReverseOn: d.hand ? '' : reverseOn,
    // A party changed in the grid is no longer the registered partner the id names, so the id goes with it.
    lines: kept.map(r => ({ ...(r.id ? { id: r.id } : {}), accountId: r.accountId, dr: satang(r.dr), cr: satang(r.cr),
      partnerId: r.partnerName.trim() === r.origPartner.trim() ? r.partnerId : '', partnerName: r.partnerName.trim(), docRef: r.docRef.trim(), narration: r.narration.trim() })),
    ...(reason.trim() ? { reason: reason.trim() } : {}),
  } : null);
  const accountIds = useMemo(() => new Set(accounts.map(a => a.id)), [accounts]);
  const problems: string[] = [];
  if (d && scope === 'full') {
    const month = d.jv?.month || '';
    const from = month && `${month}-01` > d.periodStart ? `${month}-01` : d.periodStart;
    const to = month && lastDay(month) < d.periodEnd ? lastDay(month) : d.periodEnd;
    if (!narration.trim()) problems.push(t('pa_edit_need_narration'));
    if (kept.length < 2 || kept.some(r => !r.accountId || (satang(r.dr) > 0) === (satang(r.cr) > 0) || satang(r.dr) < 0 || satang(r.cr) < 0)) problems.push(t('pa_edit_need_lines'));
    if ((d.jv || !docNo.trim()) && !calcNote.trim()) problems.push(t('pa_edit_need_calc'));
    if (!d.jv && /^JV-/i.test(docNo.trim())) problems.push(t('pa_edit_jv_typed'));
    if (book === 'bank' && !bankAcct.trim()) problems.push(t('pa_edit_need_bank'));
    if (!date || date < from || date > to) {
      problems.push(month && date.slice(0, 7) !== month ? t('pa_edit_jv_month', { docNo: d.docNo, month: `${month.slice(5, 7)}/${month.slice(0, 4)}` })
        : t('pa_edit_date_out', { from: d.periodStart, to: d.periodEnd }));
    }
    if (!d.hand && reverseOn && reverseOn <= d.periodEnd) problems.push(t('pa_edit_reverse_inside', { to: d.periodEnd }));
    for (const r of kept) {
      // An account the picker does not list (switched off, of a type nothing is posted to, gone) is refused by New's rules.
      if (r.accountId && accounts.length && !accountIds.has(r.accountId)) {
        problems.push(t(PROBLEM_KEY[r.problem] || 'pa_edit_account_not_postable', { code: r.code || '—' }));
        break;
      }
    }
  }
  // The hand door's lock on these lines: held where it would refuse them (the New form's rule, ./handPrepaid).
  const prepaid = prepaidHoldOf(handLock, kept.map(r => ({ accountId: r.accountId, drSat: satang(r.dr) })));
  const handReady = !handLine || (handLock !== undefined && !prepaid.hit.length);
  const bodyKey = JSON.stringify(body());
  const refusedAgain = !!held && held === bodyKey;
  // review: the legs whose account was changed here
  const changes = d && scope === 'review' ? d.lines.filter(l => l.free && chosen[l.id] && chosen[l.id] !== l.accountId) : [];
  const ready = !readOnly && !busy && dirty && (scope === 'full' ? balanced && !problems.length && handReady && !refusedAgain : scope === 'review' ? changes.length > 0 : false);

  const save = async () => {
    if (!d || !ready) return;
    setBusy(true); setErr(''); setNote('');
    try {
      if (scope === 'full') {
        const b = body()!;
        const r = await api.jnlEditDraft(entryId, b);
        onSaved(t('pa_edit_saved', { docNo: r.docNo }));
        return;
      }
      for (const l of changes) await api.jnlSetLineAccount(l.id, chosen[l.id]);
      onSaved(t('pa_edit_saved', { docNo: d.docNo }));
    } catch (e: any) {
      const status = Number(e?.status) || 0;
      if (e?.payload?.code === 'draft_changed') { await load(); setNote(t('pa_edit_changed_elsewhere')); }
      else {
        setErr(String(e?.message || e));
        if (status >= 400 && status < 500) setHeld(bodyKey);
        if (scope === 'review') { await load(); onChanged?.(); }
      }
    } finally { setBusy(false); }
  };

  const accountLabel = (a: { code: string; name_th: string; name_en: string }) => `${a.code} · ${a.name_th}${a.name_en ? ` — ${a.name_en}` : ''}`;
  const options = (current: string, onlyFree: boolean): React.ReactNode[] => {
    const list = accounts.filter(a => !onlyFree || a.free || a.id === current);
    const out = list.map(a => <option key={a.id} value={a.id}>{accountLabel(a)}</option>);
    if (current && !accountIds.has(current)) {
      const l = d?.lines.find(x => x.accountId === current);
      out.unshift(<option key={current} value={current}>{l ? `${l.code} · ${l.nameTh || l.name}` : '—'}</option>);
    }
    return out;
  };

  const title = t('pa_draft_edit_title');
  // TL-13: the number beside the title only while it is not a field (a JV number, or a generated draft's).
  const shownNo = d && (d.jv || scope !== 'full') ? d.docNo : '';
  const footer = d && scope ? <Foot onClose={onClose} onSubmit={save} busy={busy} ready={ready} icon="ti ti-device-floppy" label={t('pa_edit_save')} t={t} /> : undefined;

  return (
    <Dialog fullscreen title={title} docNo={shownNo} onClose={onClose} busy={busy} dirty={dirty} footer={footer}>
      <div data-draft-edit={scope || 'none'} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loadErr && <div role="alert" style={warnS}>{loadErr}</div>}
        {!d && !loadErr && <Loader />}
        {note && <div data-edit-note style={noteS}><i className="ti ti-refresh" aria-hidden /> {note}</div>}
        {d && !scope && (
          <div data-edit-none={d.edit.code || ''} title={d.edit.message} style={noteS}>
            <i className="ti ti-lock" aria-hidden /> {d.edit.code && EDIT_REF_KEY[d.edit.code] ? t(EDIT_REF_KEY[d.edit.code], { defaultValue: d.edit.message }) : d.edit.message}
          </div>
        )}
        {d?.hand && (
          <div data-edit-hand={d.hand.kind} style={noteS}>
            <i className="ti ti-link" aria-hidden /> {t('pa_draft_made_for', { what: HAND_FOR_KEY[d.hand.kind] ? t(HAND_FOR_KEY[d.hand.kind], { label: d.hand.label }) : d.hand.label })}
          </div>
        )}

        {d && scope === 'full' && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ minWidth: 150 }}><span style={lblS}>{t('pa_edit_date')}</span>
                <input type="date" value={date} data-edit-field="date"
                  min={d.jv && `${d.jv.month}-01` > d.periodStart ? `${d.jv.month}-01` : d.periodStart}
                  max={d.jv && lastDay(d.jv.month) < d.periodEnd ? lastDay(d.jv.month) : d.periodEnd}
                  onChange={e => { touch(); setDate(e.target.value); }} style={inpS} /></label>
              <label style={{ minWidth: 170 }}><span style={lblS}>{t('pa_edit_book')}</span>
                <select value={book} data-edit-field="book" onChange={e => { touch(); setBook(e.target.value); }} style={inpS}>
                  {BOOKS.map(([k, key]) => <option key={k} value={k}>{t(key)}</option>)}
                </select></label>
              {book === 'bank' && (
                <label style={{ minWidth: 190 }}><span style={lblS}>{t('pa_edit_bank_acct')}</span>
                  <input value={bankAcct} list="draft-edit-bank-accts" data-edit-field="bank" onChange={e => { touch(); setBankAcct(e.target.value); }} style={inpS} />
                  <datalist id="draft-edit-bank-accts">{bankAccts.map(a => <option key={a} value={a} />)}</datalist></label>
              )}
              {d.jv ? (
                <div style={{ minWidth: 190 }}><span style={lblS}>{t('pa_edit_docno_jv')}</span><div data-edit-jv style={factS}>{d.docNo}</div></div>
              ) : (
                <label style={{ minWidth: 190 }}><span style={lblS}>{t('pa_edit_docno')}</span>
                  <input value={docNo} data-edit-field="docNo" onChange={e => { touch(); setDocNo(e.target.value); }} style={inpS} /></label>
              )}
              {!d.hand && (
                <label style={{ minWidth: 150 }}><span style={lblS}>{t('pa_edit_reverse_on')}</span>
                  <input type="date" value={reverseOn} data-edit-field="reverseOn" onChange={e => { touch(); setReverseOn(e.target.value); }} style={inpS} /></label>
              )}
            </div>
            {d.jv && <div data-edit-jv-month style={noteS}>{t('pa_edit_jv_month', { docNo: d.docNo, month: `${d.jv.month.slice(5, 7)}/${d.jv.month.slice(0, 4)}` })}</div>}
            <label><span style={lblS}>{t('pa_edit_narration')}</span>
              <input value={narration} data-edit-field="narration" onChange={e => { touch(); setNarration(e.target.value); }} style={inpS} /></label>
            <label><span style={lblS}>{t('pa_edit_calcnote')}</span>
              <input value={calcNote} data-edit-field="calcNote" onChange={e => { touch(); setCalcNote(e.target.value); }} style={inpS} /></label>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead><tr>
                  <th style={{ ...lblS, textAlign: 'left', minWidth: 240 }}>{t('pa_edit_account')}</th>
                  <th style={{ ...lblS, textAlign: 'right', width: 120 }}>{t('pa_edit_dr')}</th>
                  <th style={{ ...lblS, textAlign: 'right', width: 120 }}>{t('pa_edit_cr')}</th>
                  <th style={{ ...lblS, textAlign: 'left', minWidth: 150 }}>{t('pa_edit_partner')}</th>
                  <th style={{ ...lblS, textAlign: 'left', minWidth: 120 }}>{t('pa_edit_ref')}</th>
                  <th style={{ ...lblS, textAlign: 'left', minWidth: 170 }}>{t('pa_edit_memo')}</th>
                  <th style={{ width: 34 }} />
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key} data-edit-row={r.id || 'new'}>
                      <td style={cell}>
                        <select value={r.accountId} data-edit-cell="account" onChange={e => setRow(r.key, { accountId: e.target.value, problem: '' })} style={inpS}>
                          <option value="" />
                          {options(r.accountId, false)}
                        </select>
                      </td>
                      {/* One side per line: typing in one clears the other — the ledger's own CHECK. */}
                      <td style={cell}><input value={r.dr} inputMode="decimal" data-edit-cell="dr" onChange={e => setRow(r.key, { dr: e.target.value, cr: '' })} style={{ ...inpS, textAlign: 'right' }} /></td>
                      <td style={cell}><input value={r.cr} inputMode="decimal" data-edit-cell="cr" onChange={e => setRow(r.key, { cr: e.target.value, dr: '' })} style={{ ...inpS, textAlign: 'right' }} /></td>
                      <td style={cell}><input value={r.partnerName} data-edit-cell="partner" onChange={e => setRow(r.key, { partnerName: e.target.value })} style={inpS} /></td>
                      <td style={cell}><input value={r.docRef} placeholder={d.jv ? d.docNo : docNo} data-edit-cell="ref" onChange={e => setRow(r.key, { docRef: e.target.value })} style={inpS} /></td>
                      <td style={cell}><input value={r.narration} placeholder={narration} data-edit-cell="memo" onChange={e => setRow(r.key, { narration: e.target.value })} style={inpS} /></td>
                      <td style={{ ...cell, textAlign: 'right' }}>
                        {rows.length > 2 && (
                          <button type="button" title={t('pa_edit_remove_line')} aria-label={t('pa_edit_remove_line')} data-edit-remove
                            onClick={() => { touch(); setRows(rs => rs.filter(x => x.key !== r.key)); }} style={{ ...smallBtn, color: '#B42318' }}>
                            <i className="ti ti-x" aria-hidden />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td style={cell}>
                    <button type="button" data-edit-add onClick={() => { touch(); setRows(rs => [...rs, { key: `r${++rowSeq}`, id: '', accountId: '', dr: '', cr: '', partnerId: '', partnerName: '',
                      origPartner: '', docRef: '', narration: '', problem: '', code: '' }]); }} style={smallBtn}>
                      <i className="ti ti-plus" aria-hidden /> {t('pa_edit_add_line')}
                    </button>
                  </td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} data-edit-total="dr">{baht(dr)}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} data-edit-total="cr">{baht(cr)}</td>
                  <td colSpan={4} style={cell}>
                    <span data-edit-balance={balanced ? 'yes' : 'no'} style={{ fontSize: 12, fontWeight: 700, color: balanced ? '#166534' : '#B42318' }}>
                      {balanced ? t('pa_edit_balanced') : t('pa_edit_unbalanced', { diff: baht(Math.abs(dr - cr)) })}
                    </span>
                  </td>
                </tr></tfoot>
              </table>
            </div>
            <label><span style={lblS}>{t('pa_edit_why')}</span>
              <textarea value={reason} maxLength={500} rows={2} data-edit-field="reason" onChange={e => { touch(); setReason(e.target.value); }} style={{ ...inpS, resize: 'vertical' }} /></label>
            {dirty && problems.length > 0 && (
              <div data-edit-problems style={warnS}>{problems.map((p, i) => <div key={i}>{p}</div>)}</div>
            )}
            {!!prepaid.hit.length && handLock && (
              <div data-edit-prepaid={prepaid.caseKey} title={handLock.message} style={warnS}>
                <i className="ti ti-alert-triangle" aria-hidden /> <PrepaidSentence lock={handLock} hold={prepaid}
                  codeOf={id => accounts.find(a => a.id === id)?.code || rows.find(r => r.accountId === id)?.code || ''} />
              </div>
            )}
            {/* …and, as on the New form, where part of the withholding is still left for this line and the lines carry none of
                it: how much it may carry — a hint, never a refusal. */}
            {handLock && handLock.case === 'part' && !prepaid.debits && Number(handLock.roomSat || 0) > 0 && (
              <div data-edit-prepaid-room={Number(handLock.roomSat || 0)} style={noteS}>
                <i className="ti ti-info-circle" aria-hidden /> {t('pa_manual_prepaid_room_hint', { receipt: handLock.params?.receipt || '—', room: baht(Number(handLock.roomSat || 0)) })}
              </div>
            )}
            {refusedAgain && <div data-edit-held style={warnS}>{t('pa_edit_refused_again')}</div>}
          </>
        )}

        {d && scope === 'review' && (
          <>
            <div data-edit-review-hint style={noteS}><i className="ti ti-info-circle" aria-hidden /> {t('pa_edit_review_hint')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{d.narration}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr>
                <th style={{ ...lblS, textAlign: 'left' }}>{t('pa_edit_account')}</th>
                <th style={{ ...lblS, textAlign: 'left' }}>{t('pa_edit_partner')}</th>
                <th style={{ ...lblS, textAlign: 'right' }}>{t('pa_edit_dr')}</th>
                <th style={{ ...lblS, textAlign: 'right' }}>{t('pa_edit_cr')}</th>
                <th />
              </tr></thead>
              <tbody>
                {d.lines.map(l => (
                  <React.Fragment key={l.id}>
                    <tr data-edit-leg={l.free ? 'free' : 'locked'} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={cell}>
                        {l.free && !readOnly ? (
                          <select value={chosen[l.id] || l.accountId} data-edit-cell="account"
                            onChange={e => { touch(); setChosen(c => ({ ...c, [l.id]: e.target.value })); }} style={inpS}>
                            {options(l.accountId, true)}
                          </select>
                        ) : (
                          <span style={{ color: 'var(--text-primary)' }}>{l.code ? `${l.code} · ${l.nameTh || l.name}${l.nameEn ? ` — ${l.nameEn}` : ''}` : '—'}</span>
                        )}
                      </td>
                      <td style={cell}>{l.partnerName}</td>
                      <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.dr ? baht(l.dr) : ''}</td>
                      <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.cr ? baht(l.cr) : ''}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>
                        {l.free && !readOnly && !dirty && (
                          <button type="button" data-edit-split onClick={() => setSplitting(s => (s === l.id ? '' : l.id))} style={smallBtn}>
                            <i className="ti ti-arrows-split-2" aria-hidden /> {t('pa_split_btn')}
                          </button>
                        )}
                      </td>
                    </tr>
                    {splitting === l.id && (
                      <tr><td colSpan={5} style={{ padding: '0 5px 10px' }}>
                        <SplitLeg line={{ id: l.id, account_id: l.accountId, dr: l.dr, cr: l.cr, code: l.code, name_th: l.nameTh, name_en: l.nameEn }}
                          accounts={accounts.filter(a => a.free) as any}
                          onSaved={async (m: string) => { setSplitting(''); setNote(m); await load(); onChanged?.(); }}
                          onCancel={() => setSplitting('')} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </>
        )}
        {err && <div role="alert" data-edit-err style={warnS}>{err}</div>}
        {/* (Owner 2026-11-02, the requirement's "every document detail or editing page") A draft takes its support here too. An upload stands by itself, and nothing typed above is sent by it. */}
        {!!d && <RecordEvidence clientId={d.clientId} kind="journal_entry" id={d.entryId} />}
      </div>
    </Dialog>
  );
}
